/**
 * 无限画布前端 API 客户端
 *
 * 请求底层统一委托 platform/http（鉴权头、401 清会话与广播由平台层处理），
 * 此处只保留画布特有的错误文案与 409 冲突哨兵逻辑。
 */
import { platformFetch, PlatformApiError } from '../../../platform/http';
import type {
  CanvasBackground,
  CanvasEdge,
  CanvasNode,
  CanvasProjectFull,
  CanvasProjectMeta,
  CanvasTheme,
} from '../types';

async function authFetch(path: string, options: RequestInit = {}): Promise<Response> {
  return platformFetch(path, options);
}

async function readJsonOrThrow<T>(response: Response, fallbackError: string): Promise<T> {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const message = (data && (data.error || data.msg)) || fallbackError;
    throw new PlatformApiError(message, response.status, (data as { code?: string } | null)?.code);
  }
  return (data?.data ?? data) as T;
}

export interface CanvasSnapshotPayload {
  name?: string;
  viewportX?: number;
  viewportY?: number;
  zoom?: number;
  background?: CanvasBackground;
  theme?: CanvasTheme;
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  /** 乐观锁：前端最后一次加载 / 保存成功时拿到的 project.updatedAt，用于多标签页冲突检测 */
  baseUpdatedAt?: string;
}

/** 画布保存冲突（其他窗口已修改）的哨兵错误 message */
export const CANVAS_SAVE_CONFLICT = '__canvas_save_conflict__';

/** 视频组合成任务视图（后端 VideoConcatJob 的对外形态） */
export interface VideoConcatJobView {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  /** downloading / transcoding / concat / uploading */
  stage: string | null;
  errorMessage: string | null;
  /** 完成后的持久视频地址 */
  url: string | null;
  durationSec: number | null;
  width: number | null;
  height: number | null;
}

/** 视频剪辑任务视图（后端 VideoEditJob 的对外形态） */
export interface VideoEditJobView {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  /** downloading / processing / uploading */
  stage: string | null;
  errorMessage: string | null;
  operation: 'trim' | 'split' | 'speed';
  /** 结果1（trim/speed/spilt第一段） */
  url: string | null;
  /** 结果2（split第二段） */
  url2: string | null;
  durationSec: number | null;
  durationSec2: number | null;
}

export interface CanvasCreateInput {
  name?: string;
  background?: CanvasBackground;
  theme?: CanvasTheme;
}

export interface CanvasUpdateInput {
  name?: string;
  viewportX?: number;
  viewportY?: number;
  zoom?: number;
  background?: CanvasBackground;
  theme?: CanvasTheme;
  /** 归档 / 取消归档 */
  isHidden?: boolean;
}

export const canvasAPI = {
  // 列出项目（不含节点）；archived=true 返回归档列表
  async listProjects(archived = false): Promise<CanvasProjectMeta[]> {
    const response = await authFetch(`/canvas/projects${archived ? '?archived=1' : ''}`);
    return readJsonOrThrow<CanvasProjectMeta[]>(response, '列出画布失败');
  },

  async createProject(input: CanvasCreateInput = {}): Promise<CanvasProjectMeta> {
    const response = await authFetch('/canvas/projects', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return readJsonOrThrow<CanvasProjectMeta>(response, '新建画布失败');
  },

  async getProject(id: string): Promise<CanvasProjectFull> {
    const response = await authFetch(`/canvas/projects/${encodeURIComponent(id)}`);
    return readJsonOrThrow<CanvasProjectFull>(response, '读取画布失败');
  },

  async updateProject(id: string, input: CanvasUpdateInput): Promise<CanvasProjectMeta> {
    const response = await authFetch(`/canvas/projects/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    return readJsonOrThrow<CanvasProjectMeta>(response, '更新画布失败');
  },

  async deleteProject(id: string): Promise<void> {
    const response = await authFetch(`/canvas/projects/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error((data && data.error) || '删除画布失败');
    }
  },

  async duplicateProject(id: string): Promise<CanvasProjectMeta> {
    const response = await authFetch(`/canvas/projects/${encodeURIComponent(id)}/duplicate`, {
      method: 'POST',
    });
    return readJsonOrThrow<CanvasProjectMeta>(response, '复制画布失败');
  },

  async saveSnapshot(id: string, payload: CanvasSnapshotPayload): Promise<{ updatedAt: string }> {
    const response = await authFetch(`/canvas/projects/${encodeURIComponent(id)}/snapshot`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      if (response.status === 409) {
        throw new Error(CANVAS_SAVE_CONFLICT);
      }
      throw new Error((data && data.error) || '保存画布失败');
    }
    const data = await response.json().catch(() => null);
    return { updatedAt: (data?.data?.updatedAt as string | undefined) ?? '' };
  },

  // ---------------- 视频组合成（导出为一段视频） ----------------

  /** 提交视频组合成任务（urls 顺序即拼接顺序），返回后台任务 ID */
  async submitVideoConcat(urls: string[]): Promise<{ jobId: string }> {
    const response = await authFetch('/canvas/videos/concat', {
      method: 'POST',
      body: JSON.stringify({ urls }),
    });
    return readJsonOrThrow<{ jobId: string }>(response, '提交视频合成任务失败');
  },

  /** 查询视频组合成任务进度 / 结果 */
  async getVideoConcatJob(jobId: string): Promise<VideoConcatJobView> {
    const response = await authFetch(`/canvas/videos/concat/${encodeURIComponent(jobId)}`);
    return readJsonOrThrow<VideoConcatJobView>(response, '查询视频合成任务失败');
  },

  // ---------------- 视频剪辑（裁剪 / 分割 / 变速） ----------------

  /** 提交视频剪辑任务，返回后台任务 ID */
  async submitVideoEdit(input: {
    inputUrl: string;
    operation: 'trim' | 'split' | 'speed';
    params: Record<string, number>;
  }): Promise<{ jobId: string }> {
    const response = await authFetch('/canvas/videos/edit', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return readJsonOrThrow<{ jobId: string }>(response, '提交视频剪辑任务失败');
  },

  /** 查询视频剪辑任务进度 / 结果 */
  async getVideoEditJob(jobId: string): Promise<VideoEditJobView> {
    const response = await authFetch(`/canvas/videos/edit/${encodeURIComponent(jobId)}`);
    return readJsonOrThrow<VideoEditJobView>(response, '查询视频剪辑任务失败');
  },

  async exportProject(id: string): Promise<unknown> {
    const response = await authFetch(`/canvas/projects/${encodeURIComponent(id)}/export`);
    return readJsonOrThrow<unknown>(response, '导出画布失败');
  },

  async importProject(payload: unknown): Promise<CanvasProjectMeta> {
    const response = await authFetch('/canvas/projects/import', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return readJsonOrThrow<CanvasProjectMeta>(response, '导入画布失败');
  },
};
